import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Paper, CircularProgress } from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { claimPhone } from '../api/whatsapp';

const RegisterPhone = ({ account, onComplete }) => {
  const [claimPhoneNumberId, setClaimPhoneNumberId] = useState('');
  const [claimWabaId, setClaimWabaId] = useState('');
  const [claimClientToken, setClaimClientToken] = useState('');
  const [claimError, setClaimError] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);

  const handleClaimPhone = async () => {
    if (!claimPhoneNumberId.trim() || !claimWabaId.trim()) {
      setClaimError('Both Phone Number ID and WABA ID are required');
      return;
    }
    setClaimError('');
    setClaimLoading(true);
    try {
      await claimPhone(claimPhoneNumberId.trim(), claimWabaId.trim(), claimClientToken.trim());
      onComplete();
    } catch (err) {
      setClaimError(err.response?.data?.message || 'Failed to claim phone number');
    }
    setClaimLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f0f2f5', p: 3 }}>
      <Paper sx={{ maxWidth: 520, width: '100%', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#075e54', p: 3, color: '#fff', textAlign: 'center' }}>
          <SmartphoneIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Set Up Your WhatsApp Number</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Connect your number from WhatsApp Manager — no OTP needed
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#16a34a' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a' }}>
              Already have a connected number in WhatsApp Manager?
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Go to <b>WhatsApp Manager</b> → <b>Account tools</b> → <b>Phone Numbers</b>, select your connected number, and copy its <b>Phone Number ID</b>. Then open <b>Account tools</b> → <b>WhatsApp Business Account</b> and copy its <b>WABA ID</b> (both from the same account). Paste them below to activate instantly.
          </Typography>

          {claimError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{claimError}</Alert>}

          <TextField label="Phone Number ID" fullWidth required
            value={claimPhoneNumberId}
            onChange={e => setClaimPhoneNumberId(e.target.value)}
            placeholder="e.g. 1191938384004414"
            helperText="Your connected number's Phone Number ID from WhatsApp Manager"
            InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
            InputLabelProps={{ sx: { fontWeight: 500 } }}
            sx={{ mb: 2 }}
          />
          <TextField label="WABA ID" fullWidth required
            value={claimWabaId}
            onChange={e => setClaimWabaId(e.target.value)}
            placeholder="e.g. 1004781542329612"
            helperText="Your WhatsApp Business Account ID — must belong to the same account as the number"
            InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
            InputLabelProps={{ sx: { fontWeight: 500 } }}
            sx={{ mb: 2 }}
          />
          <TextField label="Business Manager Access Token (required)" fullWidth
            value={claimClientToken}
            onChange={e => setClaimClientToken(e.target.value)}
            placeholder="System user token of your Business Manager"
            helperText="Your number's Business Manager needs this so we can operate your number. Generate it from BM → Settings → Users → System users → Add → Generate token (permissions: whatsapp_business_messaging + whatsapp_business_management). Without it, connection fails with 'not accessible / missing permissions'."
            InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
            InputLabelProps={{ sx: { fontWeight: 500 } }}
            sx={{ mb: 3 }}
          />

          <Button variant="contained" fullWidth onClick={handleClaimPhone}
            disabled={claimLoading || !claimPhoneNumberId.trim() || !claimWabaId.trim()}
            startIcon={claimLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <CheckCircleIcon />}
            sx={{
              borderRadius: '14px', textTransform: 'none', py: 1.4, fontSize: 16, fontWeight: 700,
              background: 'linear-gradient(135deg, #075e54, #0a8a7a)',
              '&:hover': { background: 'linear-gradient(135deg, #054d44, #087566)' },
              '&.Mui-disabled': { bgcolor: '#94a3b8' },
            }}>
            {claimLoading ? 'Activating...' : 'Verify & Activate Account'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default RegisterPhone;
